import React from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { PlayCircle, StopCircle, Clock, Loader2, AlertCircle, Timer, Download } from "lucide-react";

/**
 * v23.21 — Driver shift / rota panel. Free-form clock-in / clock-out, live
 * timer while on shift, last 5 shifts + total hours worked.
 */
function fmtDuration(secs) {
  if (!secs && secs !== 0) return "—";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

function fmtDurationHMS(secs) {
  if (!secs && secs !== 0) return "—";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  return `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
}

export default function DriverShiftPanel() {
  const [current, setCurrent] = React.useState(null);
  const [shifts, setShifts] = React.useState([]);
  const [totalHours, setTotalHours] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [acting, setActing] = React.useState(false);
  const [error, setError] = React.useState("");
  const [now, setNow] = React.useState(Date.now());

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [c, l] = await Promise.all([
        apiClient.get("/driver/shifts/current"),
        apiClient.get("/driver/shifts"),
      ]);
      setCurrent(c.data.shift);
      setShifts((l.data.shifts || []).slice(0, 5));
      setTotalHours(l.data.total_hours || 0);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load shifts");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    if (!current) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [current]);

  const liveSeconds = React.useMemo(() => {
    if (!current?.started_at) return 0;
    return Math.max(0, Math.floor((now - new Date(current.started_at).getTime()) / 1000));
  }, [current, now]);

  const clockIn = async () => {
    setActing(true); setError("");
    try {
      await apiClient.post("/driver/shifts/clock-in", { note: null });
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to start shift");
    } finally { setActing(false); }
  };

  const clockOut = async () => {
    if (!window.confirm("End your shift now? This locks the timesheet entry.")) return;
    setActing(true); setError("");
    try {
      await apiClient.post("/driver/shifts/clock-out");
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to end shift");
    } finally { setActing(false); }
  };

  const [exporting, setExporting] = React.useState(false);
  const exportCsv = async () => {
    setExporting(true); setError("");
    try {
      // Ask for this week's timesheet as CSV, trigger a browser download.
      const res = await apiClient.get("/driver/shifts/export.csv", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const disp = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(disp);
      a.download = match ? match[1] : `raanah_timesheet_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to export timesheet");
    } finally { setExporting(false); }
  };

  return (
    <section className="mb-8" data-testid="driver-shift-panel">
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A] inline-flex items-center gap-2">
            <Timer size={20} /> Shift timesheet
          </h2>
          <p className="mt-1 text-sm text-[#475569]">Log your working hours each day for your own records / claims.</p>
        </div>
        <div className="flex items-center gap-4">
          <p className="text-xs text-[#475569]" data-testid="driver-shift-total">
            Lifetime: <strong>{totalHours}h</strong>
          </p>
          <button
            type="button"
            onClick={exportCsv}
            disabled={exporting}
            data-testid="shift-export-csv-button"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.14em] font-bold text-[#1E4D55] hover:text-[#163C42] disabled:opacity-60"
          >
            {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            This week (CSV)
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3" data-testid="shift-error">
          <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      <div className={`p-5 border ${current ? "bg-[#5BB247]/5 border-[#5BB247]/40" : "bg-white border-[#0A0A0A]/10"}`}>
        {loading ? (
          <p className="text-sm text-[#475569] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p>
        ) : current ? (
          <div className="flex items-center gap-4 flex-wrap" data-testid="shift-active-card">
            <span className="inline-flex h-2.5 w-2.5 rounded-full bg-[#5BB247] animate-pulse" />
            <div className="flex-1 min-w-[220px]">
              <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#2c8642]">On shift</p>
              <p className="mt-1 font-display text-xl font-bold tracking-tight text-[#0A0A0A]" data-testid="shift-live-timer">
                {fmtDurationHMS(liveSeconds)}
              </p>
              <p className="text-xs text-[#475569]">Started {new Date(current.started_at).toLocaleTimeString("en-GB")}</p>
            </div>
            <button
              onClick={clockOut}
              disabled={acting}
              data-testid="shift-clock-out-button"
              className="inline-flex items-center gap-2 bg-[#B91C1C] hover:bg-[#991B1B] text-white px-5 py-3 text-sm font-semibold disabled:opacity-60"
            >
              {acting ? <Loader2 size={14} className="animate-spin" /> : <StopCircle size={16} />}
              End shift
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#475569]">Not on shift</p>
              <p className="mt-1 text-sm text-[#0A0A0A]">Tap Start shift when you begin your working day.</p>
            </div>
            <button
              onClick={clockIn}
              disabled={acting}
              data-testid="shift-clock-in-button"
              className="inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-5 py-3 text-sm font-semibold disabled:opacity-60"
            >
              {acting ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={16} />}
              Start shift
            </button>
          </div>
        )}
      </div>

      {shifts.length > 0 && (
        <div className="mt-6" data-testid="shift-history">
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">Last 5 shifts</p>
          <div className="space-y-1.5">
            {shifts.map((s) => (
              <div key={s.id} data-testid={`shift-row-${s.id}`} className="bg-white border border-[#0A0A0A]/10 p-3 flex items-center gap-3 flex-wrap text-sm">
                <Clock size={13} className="text-[#475569]" />
                <span className="text-[#0A0A0A] font-semibold">
                  {new Date(s.started_at).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
                </span>
                <span className="text-xs text-[#475569]">
                  {new Date(s.started_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                  {s.ended_at && ` → ${new Date(s.ended_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`}
                </span>
                <span className="ml-auto text-xs font-semibold text-[#0A0A0A]">{fmtDuration(s.duration_seconds)}</span>
                {s.auto_closed && (
                  <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#A66800] border border-[#F5A623]/40 bg-[#F5A623]/10 px-1.5 py-0.5">Auto-closed</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
